import type { ErrorCode } from '../lib/api'

/** Every text of the interface. Fix a label here, not in the components. */
export const en = {
  site: {
    name: 't.armx',
    description: 'One public chat room, 144 characters at a time.',
    contact: 'contact@armx.be',
  },
  footer: {
    terms: 'Terms',
    privacy: 'Privacy',
  },
  nav: {
    moderation: 'Moderation',
    account: 'Account',
    logOut: 'Log out',
    backToChat: 'Back to the chat',
  },
  update: {
    available: 'A new version of t.armx is available.',
    reload: 'Update',
    whatsNew: 'What’s new',
  },
  common: {
    loading: 'Loading',
    cancel: 'Cancel',
    close: 'Close',
    reason: 'Reason',
    tryAgain: 'Try again',
  },
  auth: {
    email: 'Email',
    password: 'Password',
    newPassword: 'New password',
    username: 'Username',
    passwordHint: (length: number) => `At least ${length} characters.`,
    usernameRules: ['3 to 20 characters', 'Letters, digits and underscores only', 'Can be changed once every 7 days'],
    usernameChecking: 'Checking…',
    usernameAvailable: 'This username is available.',
    usernameUnavailable: {
      invalid: 'Use 3 to 20 letters, digits or underscores.',
      reserved: 'This username is reserved.',
      taken: 'This username is taken.',
    },
    agreePrefix: 'I agree to the',
    termsLink: 'Terms of Use',
    agreeMiddle: 'and have read the',
    privacyLink: 'Privacy Policy',
    mustAgree: 'You need to agree to the terms to continue.',
    captchaFailed: 'The anti-robot check could not load. Reload the page and try again.',
    captchaPending: 'Wait for the anti-robot check to finish.',
    login: {
      title: 'Sign in',
      metaDescription: 'Sign in to t.armx, one public chat room, 144 characters at a time.',
      description: 'Good to see you again.',
      submit: 'Sign in',
      noAccount: 'No account yet?',
      createOne: 'Create one',
      forgot: 'Forgot your password?',
      unconfirmed: 'Your email address is not confirmed yet.',
      enterCode: 'Enter my code',
    },
    register: {
      title: 'Create your account',
      metaDescription: 'Create a t.armx account: one public chat room, open to anyone with a confirmed email address.',
      submit: 'Create my account',
      haveAccount: 'Already have an account?',
      signIn: 'Sign in',
      confirm: 'Create my account',
    },
    reset: {
      title: 'Reset your password',
      description: 'We will email you a code to choose a new one.',
      submit: 'Send the code',
      backToSignIn: 'Back to sign in',
      codeTitle: 'Choose a new password',
      codeDescription: (email: string) => `Enter the code we sent to ${email}.`,
      confirm: 'Change my password',
    },
    code: {
      title: 'Check your inbox',
      description: (email: string) => `We sent a 6-digit code to ${email}. It expires in 10 minutes.`,
      label: 'Verification code',
      hint: 'Paste it in one go, or type it.',
      resend: 'Send a new code',
      resent: 'A new code is on its way.',
    },
    errors: {
      invalid_credentials: 'Wrong email or password.',
      email_not_confirmed: 'Your email address is not confirmed yet.',
      captcha_failed: 'The anti-robot check failed. Try again.',
      weak_password: 'This password is too weak. Use at least 10 characters.',
      same_password: 'Choose a password different from the old one.',
      invalid_code: 'This code is wrong or has expired.',
      rate_limited: 'Too many attempts. Wait a few minutes and try again.',
      signup_failed: 'This username was taken in the meantime. Pick another one.',
      unknown: 'Something went wrong. Try again.',
    },
  },
  chat: {
    title: 'Chat',
    room: 'Public room',
    roomDescription: 'One room, everyone in it, 144 characters at a time.',
    placeholder: 'Say something',
    loadOlder: 'Load older messages',
    emptyTitle: 'Nothing here yet',
    emptyDescription: 'Be the first to say something. Messages are public and limited to 144 characters.',
    loadFailed: 'The messages could not be loaded.',
    actions: 'Message actions',
    report: 'Report',
    delete: 'Delete',
    ban: 'Ban user',
    modBadge: 'mod',
    deletedUser: 'deleted user',
    today: 'Today',
    yesterday: 'Yesterday',
    offline: 'You are offline. Messages will show up once you are back.',
    banned: (until: string | null, reason: string | null) =>
      `You are banned ${until ? `until ${until}` : 'permanently'}.${reason ? ` Reason: ${reason}` : ''}`,
    reportTitle: 'Report this message',
    reportDescription: 'A moderator will look at it. The author is not told who reported it.',
    reportConfirm: 'Send report',
    reported: 'Report sent',
    deleteTitle: 'Delete this message?',
    deleteDescription: 'It disappears for everyone. This cannot be undone.',
    deleted: 'Message deleted',
    banTitle: (username: string) => `Ban ${username}?`,
    banDescription: 'They can still read the chat, but not write.',
    banConfirm: 'Ban',
    banDuration: 'Duration',
    banReasonHint: 'Shown to the user.',
    banned_toast: 'User banned',
    durations: {
      '1h': '1 hour',
      '24h': '24 hours',
      '7d': '7 days',
      '30d': '30 days',
      forever: 'Permanently',
    },
  },
  account: {
    title: 'Your account',
    memberSince: (date: string, count: number) =>
      `Member since ${date} · ${count.toLocaleString('en-GB')} ${count === 1 ? 'message' : 'messages'}`,
    bannedBadge: 'banned',
    bannedTitle: (until: string | null) => (until ? `You are banned until ${until}` : 'You are banned permanently'),
    bannedBody: (reason: string | null) => `${reason ? `Reason: ${reason}. ` : ''}You can still read the chat.`,
    usernameTitle: 'Username',
    usernameDescription: 'Shown next to your messages, old ones included.',
    usernameNext: (date: string) => `Next change possible on ${date}.`,
    usernameRule: 'You can change it once every 7 days.',
    usernameHint: '3 to 20 characters: letters, digits and underscores.',
    save: 'Save',
    usernameSaved: 'Username updated',
    exportTitle: 'Export your data',
    exportDescription: 'A JSON file with your account details and every message you sent.',
    export: 'Export',
    deleteTitle: 'Delete your account',
    deleteDescription: 'Your account is removed for good. Your messages stay, signed “deleted user”.',
    delete: 'Delete account',
    deleteConfirmTitle: 'Delete your account?',
    deleteConfirmDescription: 'This cannot be undone. Type your username to confirm.',
    deleteConfirm: 'Delete my account',
  },
  moderation: {
    title: 'Moderation',
    description: 'Reports wait here until a moderator handles them.',
    reportsTitle: 'Pending reports',
    reportsDescription: 'Oldest first.',
    open: (count: number) => `${count} open`,
    noReports: 'No pending report',
    noReportsDescription: 'Reported messages show up here until a moderator handles them.',
    reportedBy: (who: string | null, when: string) => `Reported by ${who ?? 'a deleted user'} at ${when}`,
    dismiss: 'Dismiss',
    deleteMessage: 'Delete message',
    ban: (username: string) => `Ban ${username}`,
    bansTitle: 'Active bans',
    bansDescription: 'Banned users can still read the chat, but not write.',
    active: (count: number) => `${count} active`,
    noBans: 'Nobody is banned',
    user: 'User',
    reason: 'Reason',
    until: 'Until',
    actions: 'Actions',
    permanent: 'permanent',
    liftBan: 'Lift ban',
    lifted: 'Ban lifted',
    dismissed: 'Report dismissed',
  },
  notFound: {
    title: 'This page does not exist',
    description: 'The link may be broken, or the page may have been removed.',
  },
  /** Messages for the error codes of the API. */
  errors: {
    unauthorized: 'Your session has expired. Sign in again.',
    forbidden: 'Only moderators can do this.',
    not_found: 'This no longer exists.',
    invalid_content: 'A message holds 1 to 144 characters of plain text.',
    invalid_reason: 'Give a reason of 1 to 500 characters.',
    invalid_ban_end: 'The end of the ban must be in the future.',
    username_invalid: 'Use 3 to 20 letters, digits or underscores.',
    username_reserved: 'This username is reserved.',
    username_taken: 'This username is taken.',
    username_cooldown: 'You can change your username once every 7 days.',
    cannot_ban: 'Moderators cannot be banned.',
    rate_limited: 'Slow down a little and try again in a few seconds.',
    banned: 'You are banned and cannot write.',
    unavailable: 'The server cannot be reached. Check your connection and try again.',
    internal: 'Something went wrong on our side. Try again.',
  } satisfies Record<ErrorCode, string>,
}
